/**
 * @file   src\helpers\getPropertyIcons.js
 * @brief  This file is responsible for share want ad functionality
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import LotIcon from "../images/lot-icon.svg";
import HouseTagIcon from "../images/property-icon-house.svg";
import ApartmentIcon from "../images/property-icon-apartment.svg";
import GuestHouseIcon from "../images/property-icon-guesthouse.svg";
import TownHomeIcon from "../images/property-icon-townhome.svg";
import ManufacturedIcon from "../images/property-icon-manufactured.svg";
import MultiFamilyComplexIcon from "../images/property-icon-multifamilycomplex.svg";
import HotelIcon from "../images/property-icon-hotel.svg";
import VillaIcon from "../images/property-icon-villa.svg";
import PrivateRoomIcon from "../images/property-icon-privateroom.svg";
import OtherIcon from "../images/property-icon-other.svg";
import { setLotSizeValue, wantAdSqftValue } from "./createWantAd";

export const getPropertyIcon = (propertyId) => {
  let icon;
  switch (propertyId) {
    case 1:
      icon = HouseTagIcon;
      break;
    case 2:
      icon = ApartmentIcon;
      break;
    case 3:
      icon = TownHomeIcon;
      break;
    case 4:
      icon = MultiFamilyComplexIcon;
      break;
    case 5:
      icon = ManufacturedIcon;
      break;
    case 6:
      icon = LotIcon;
      break;
    case 7:
      icon = OtherIcon;
      break;
    case 8:
      icon = GuestHouseIcon;
      break;
    case 9:
      icon = VillaIcon;
      break;
    case 11:
      icon = PrivateRoomIcon;
      break;
    case 12:
      icon = HotelIcon;
      break;
    // RESO new types - reuse closest matching icons
    case 14: // Duplex
    case 15: // Triplex
    case 16: // Fourplex
      icon = MultiFamilyComplexIcon;
      break;
    case 17: // Vacant Land
    case 18: // Agricultural
    case 19: // Ranch
    case 20: // Unimproved Land
      icon = LotIcon;
      break;
    case 21: // Cabin
      icon = HouseTagIcon;
      break;
    case 22: // Farm
      icon = LotIcon;
      break;
    case 23: // Timeshare
      icon = VillaIcon;
      break;
    case 24: // Mixed-Use
      icon = MultiFamilyComplexIcon;
      break;
    default:
      icon = HouseTagIcon;
  }
  return icon;
};

export const getTooltipText = (propertyId) => {
  let text;
  switch (propertyId) {
    case 1:
      text = "Single-Family";
      break;
    case 2:
      text = "Condo/Apartment";
      break;
    case 3:
      text = "Townhome";
      break;
    case 4:
      text = "5+ Units";
      break;
    case 5:
      text = "Manufactured";
      break;
    case 6:
      text = "Lot / Land";
      break;
    case 7:
      text = "Other";
      break;
    case 8:
      text = "Guesthouse";
      break;
    case 9:
      text = "Villa";
      break;
    case 11:
      text = "Private Room";
      break;
    case 12:
      text = "Hotel";
      break;
    case 14:
      text = "Duplex";
      break;
    case 15:
      text = "Triplex";
      break;
    case 16:
      text = "Fourplex";
      break;
    case 17:
      text = "Vacant Land";
      break;
    case 18:
      text = "Agricultural";
      break;
    case 19:
      text = "Ranch";
      break;
    case 20:
      text = "Unimproved Land";
      break;
    case 21:
      text = "Cabin";
      break;
    case 22:
      text = "Farm";
      break;
    case 23:
      text = "Timeshare";
      break;
    case 24:
      text = "Mixed-Use";
      break;
    default:
      text = "Single-Family";
  }
  return text;
};

export const getCombinedText = (subtypes) => {
  if (subtypes && subtypes?.length > 0) {
    let combinedText = getTooltipText(subtypes[0]);
    if (subtypes?.length > 1) {
      for (let i = 1; i < subtypes?.length - 1; i++) {
        combinedText = combinedText + ", " + getTooltipText(subtypes[i]);
      }
      combinedText =
        combinedText + " or " + getTooltipText(subtypes[subtypes?.length - 1]);
    }
    return combinedText;
  }
};

export const getAdTypeText = (adType) => {
  if (adType === "BUY") {
    return "Buy a";
  } else if (adType === "LTR") {
    return "Rent a";
  } else if (adType === "STR") {
    return "Stay at a";
  }
};



export const getPropertyText = (adDetails) => {
  return getCombinedText(adDetails?.prptySubTypes)?.toLowerCase();
};

export const getDescriptionText = (adDetails) => {
  const prprtyType = _.get(adDetails, "prprtyType", []);
  const lotType = _.get(adDetails, "lotType", null);
  const adType = _.get(adDetails, "adType", null);
  const garageSpace = _.get(adDetails, "grgSpce", null);
  const stories = _.get(adDetails, "stories", null);
  const minLtSz = _.get(adDetails, "minLtSz", null);
  const maxLtSz = _.get(adDetails, "maxLtSz", null);
  const yearBuiltOption = _.get(adDetails, "yearBuiltOption", null);
  const yearBuiltMin = _.get(adDetails, "yrBltMin", null);
  const yearBuildMax = _.get(adDetails, "yrBltMax", null);
  const accsPvtBthRm = _.get(adDetails, "accsPvtBthRm", null)
    ? "Access to private bathroom"
    : null;
  const alwPets = _.get(adDetails, "alwPets", null) ? "Allows pets" : null;
  const pvtEntrnce = _.get(adDetails, "pvtEntrnce", null)
    ? "Access to private entrance"
    : null;
  const adaAccess = _.get(adDetails, "adaAccess", null)
    ? "Ada accessible"
    : null;
  let ltSize = "";
  ltSize = setLotSizeValue(
    _.get(adDetails, "minLtSz", null),
    _.get(adDetails, "maxLtSz", null)
  );
  let sqFt = wantAdSqftValue(
    _.get(adDetails, "sqFtMin", ""),
    _.get(adDetails, "sqFtMax", "")
  );
  let noBathRoom = _.get(adDetails, "noBathRm", "") || "Any";
  let noBedRoom = _.get(adDetails, "noBedRm", "") || "Any";
  let noBeds = _.get(adDetails, "noOfBeds", "") || "Any";
  if (noBathRoom && noBathRoom !== "Any") noBathRoom = noBathRoom + "+";
  if (noBedRoom && noBedRoom !== "Any") noBedRoom = noBedRoom + "+";
  if (noBeds && noBeds !== "Any") noBeds = noBeds + "+";
  const lotTypeText = lotType
    ? lotType === "1"
      ? "Rural"
      : "In city limits"
    : "";
  if (adType === "STR") {
    if (prprtyType == 6) {
      return lotTypeText + " lot type | " + ltSize + " lot size";
    } else if (prprtyType == 11) {
      return "";
    } else {
      return (
        noBedRoom + " beds | " + noBathRoom + " baths | " + noBeds + " beds"
      );
    }
  } else {
    if (prprtyType == 6) {
      return lotTypeText + " lot type | " + ltSize + " lot size";
    } else if (prprtyType == 11) {
      return "";
    } else {
      return noBedRoom + " beds | " + noBathRoom + " baths | " + sqFt + " sqft";
    }
  }
};
